import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faXmark, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { PDFRepositoryWidgetConfig } from '../../../../../types';
import { getWidgetIcon } from '../../../registry';

interface PDFRepositoryWidgetBuilderProps {
  config: PDFRepositoryWidgetConfig;
  onChange: (config: PDFRepositoryWidgetConfig) => void;
}

const PDFRepositoryWidgetBuilder: React.FC<PDFRepositoryWidgetBuilderProps> = ({
  config,
  onChange,
}) => {
  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <span className="text-2xl">{getWidgetIcon('pdf')}</span>
          <div>
            <h3 className="font-medium text-primary-900">PDF Repository Widget</h3>
            <p className="text-sm text-primary-700 mt-1">
              This is a system widget that allows users to store, browse, tag, and view PDF documents
              within a dashboard. PDFs are stored per dashboard instance.
            </p>
          </div>
        </div>
      </div>

      {/* How it Works */}
      <div className="bg-neutral-50 rounded-lg p-4">
        <h4 className="font-medium text-neutral-900 mb-3">How it works:</h4>
        <ul className="space-y-2 text-sm text-neutral-600">
          <li className="flex items-start gap-2">
            <span className="text-success-500 mt-0.5"><FontAwesomeIcon icon={faCheck} /></span>
            <span>When adding to a dashboard, you'll configure a unique data key to store PDFs</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-success-500 mt-0.5"><FontAwesomeIcon icon={faCheck} /></span>
            <span>Users can upload PDF files and add titles, descriptions, and tags</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-success-500 mt-0.5"><FontAwesomeIcon icon={faCheck} /></span>
            <span>Search and filter PDFs by title or tags</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-success-500 mt-0.5"><FontAwesomeIcon icon={faCheck} /></span>
            <span>View PDFs directly in the browser with navigation controls</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-success-500 mt-0.5"><FontAwesomeIcon icon={faCheck} /></span>
            <span>Navigate between PDFs using previous/next buttons</span>
          </li>
        </ul>
      </div>

      {/* Default Settings Preview */}
      <div className="border rounded-lg p-4">
        <h4 className="font-medium text-neutral-900 mb-3">Default Settings:</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className={config.allowUpload !== false ? 'text-success-500' : 'text-neutral-400'}>
              {config.allowUpload !== false ? <FontAwesomeIcon icon={faCheck} /> : <FontAwesomeIcon icon={faXmark} />}
            </span>
            <span className="text-neutral-700">Allow Upload</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={config.allowDelete !== false ? 'text-success-500' : 'text-neutral-400'}>
              {config.allowDelete !== false ? <FontAwesomeIcon icon={faCheck} /> : <FontAwesomeIcon icon={faXmark} />}
            </span>
            <span className="text-neutral-700">Allow Delete</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={config.allowTagging !== false ? 'text-success-500' : 'text-neutral-400'}>
              {config.allowTagging !== false ? <FontAwesomeIcon icon={faCheck} /> : <FontAwesomeIcon icon={faXmark} />}
            </span>
            <span className="text-neutral-700">Allow Tagging</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={config.gridView !== false ? 'text-success-500' : 'text-neutral-400'}>
              {config.gridView !== false ? <FontAwesomeIcon icon={faCheck} /> : <FontAwesomeIcon icon={faXmark} />}
            </span>
            <span className="text-neutral-700">Grid View</span>
          </div>
        </div>
      </div>

      {/* System Widget Notice */}
      <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <span className="text-warning-500"><FontAwesomeIcon icon={faTriangleExclamation} /></span>
          <p className="text-sm text-warning-800">
            This is a system widget. Configuration options are set when adding the widget to a
            dashboard. Template-level settings cannot be modified.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PDFRepositoryWidgetBuilder;
